import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Star, Search, Mail } from "lucide-react";

const VIP_TIERS = ["Standard", "Silver", "Gold", "Platinum", "Founder"];
const KYC_COLOR = {
  approved: "text-emerald-400",
  pending: "text-yellow-400",
  rejected: "text-red-400",
  none: "text-white/40",
};

// Hoisted filter/sort option arrays so React doesn't re-create them per render
const TIER_OPTIONS = ["All", ...VIP_TIERS];
const KYC_OPTIONS = ["All", "approved", "pending", "rejected", "none"];
const SORT_OPTIONS = [
  { v: "spend", l: "Spend ↓" },
  { v: "bids", l: "Bids ↓" },
  { v: "recent", l: "Recent" },
];

const CustomerCRM = () => {
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("");
  const [tierFilter, setTierFilter] = useState("All");
  const [kycFilter, setKycFilter] = useState("All");
  const [sortBy, setSortBy] = useState("spend");
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});

  const load = useCallback(() => api.get("/admin/users").then(({ data }) => setUsers(data)), []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = filter.toLowerCase();
    let arr = users.filter((u) => {
      if (q && !((u.email || "").toLowerCase().includes(q) || (u.name || "").toLowerCase().includes(q))) return false;
      if (tierFilter !== "All" && u.vip_tier !== tierFilter) return false;
      if (kycFilter !== "All" && u.kyc_status !== kycFilter) return false;
      return true;
    });
    arr.sort((a, b) => {
      if (sortBy === "spend") return (b.total_spend_usd || 0) - (a.total_spend_usd || 0);
      if (sortBy === "bids") return (b.bid_count || 0) - (a.bid_count || 0);
      if (sortBy === "recent") return new Date(b.last_active || 0) - new Date(a.last_active || 0);
      return 0;
    });
    return arr;
  }, [users, filter, tierFilter, kycFilter, sortBy]);

  // Stats
  const stats = useMemo(() => {
    const total = users.length;
    const vip = users.filter((u) => u.role === "vip" || ["Gold", "Platinum", "Founder"].includes(u.vip_tier)).length;
    const verified = users.filter((u) => u.kyc_status === "approved").length;
    const totalSpend = users.reduce((s, u) => s + (u.total_spend_usd || 0), 0);
    return { total, vip, verified, totalSpend };
  }, [users]);

  const saveEdit = async () => {
    try {
      await api.patch(`/admin/users/${editing}`, editForm);
      toast.success("Customer updated.");
      setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
  };

  return (
    <div data-testid="customer-crm" className="pb-32">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10">
        <Stat label="Total Customers" v={stats.total} />
        <Stat label="VIP / Tier 3+" v={stats.vip} />
        <Stat label="KYC Verified" v={stats.verified} />
        <Stat label="Lifetime Spend (USD)" v={`$${Intl.NumberFormat("en-US").format(Math.round(stats.totalSpend))}`} />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-8">
        <div className="relative flex-1 min-w-[260px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input data-testid="crm-search" placeholder="Search name or email…" value={filter} onChange={(e) => setFilter(e.target.value)}
            className="w-full bg-transparent border border-white/20 pl-10 pr-4 py-3 text-white focus:outline-none focus:border-gold text-sm" />
        </div>
        <Select v={tierFilter} on={setTierFilter} label="Tier" options={TIER_OPTIONS} testid="crm-tier-filter" />
        <Select v={kycFilter} on={setKycFilter} label="KYC" options={KYC_OPTIONS} testid="crm-kyc-filter" />
        <Select v={sortBy} on={setSortBy} label="Sort" options={SORT_OPTIONS} testid="crm-sort" />
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-white/40 font-accent text-[10px] tracking-[0.28em] uppercase border-b border-white/10">
            <tr>
              <th className="text-left py-4">Customer</th>
              <th className="text-left">Tier</th>
              <th className="text-left">KYC</th>
              <th className="text-left">Bids</th>
              <th className="text-left">Inquiries</th>
              <th className="text-left">Lifetime Spend</th>
              <th className="text-left">Joined</th>
              <th className="text-left">Last Active</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.id} data-testid={`crm-row-${u.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                <td className="py-4">
                  <div className="font-heading text-lg">{u.name || "—"}</div>
                  <div className="text-white/50 text-xs flex items-center gap-1"><Mail size={10} />{u.email}</div>
                </td>
                <td>
                  <div className="flex items-center gap-2">
                    {["Gold","Platinum","Founder"].includes(u.vip_tier) && <Star size={12} className="text-gold fill-gold" />}
                    <span className={u.role === "admin" ? "text-gold" : u.role === "vip" ? "text-gold" : "text-white/70"}>
                      {u.vip_tier}{u.role === "admin" ? " · Admin" : ""}
                    </span>
                  </div>
                </td>
                <td className={KYC_COLOR[u.kyc_status] || "text-white/40"}>
                  <span className="capitalize">{u.kyc_status}</span>
                </td>
                <td className="text-ivory">{u.bid_count}</td>
                <td className="text-white/70">{u.inquiry_count}</td>
                <td className="text-gold font-heading">${Intl.NumberFormat("en-US").format(Math.round(u.total_spend_usd || 0))}</td>
                <td className="text-white/50 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                <td className="text-white/50 text-xs">{u.last_active ? new Date(u.last_active).toLocaleDateString() : "—"}</td>
                <td className="text-right">
                  <button data-testid={`crm-edit-${u.id}`} onClick={() => { setEditing(u.id); setEditForm({ name: u.name, role: u.role, vip_tier: u.vip_tier }); }}
                    className="font-accent text-[10px] tracking-[0.24em] uppercase text-gold hover:text-ivory px-3 py-1">
                    Manage
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="text-white/40 text-center py-12">No customers match these filters.</div>}
      </div>

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="max-w-md mx-auto my-12 bg-noir border border-gold/40 p-8" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-heading text-3xl mb-6">Manage Customer</h2>
            <div className="space-y-4">
              <div>
                <label className="text-white/50 text-xs tracking-[0.18em] font-accent uppercase mb-2 block">Name</label>
                <input data-testid="crm-f-name" value={editForm.name || ""} onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                  className="w-full bg-transparent border border-white/20 px-3 py-3 text-white focus:outline-none focus:border-gold" />
              </div>
              <div>
                <label className="text-white/50 text-xs tracking-[0.18em] font-accent uppercase mb-2 block">VIP Tier</label>
                <select data-testid="crm-f-tier" value={editForm.vip_tier || "Standard"} onChange={(e) => setEditForm({...editForm, vip_tier: e.target.value})}
                  className="w-full bg-transparent border border-white/20 px-3 py-3 text-white focus:outline-none focus:border-gold">
                  {VIP_TIERS.map((t) => <option key={t} value={t} className="bg-noir">{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-white/50 text-xs tracking-[0.18em] font-accent uppercase mb-2 block">Role</label>
                <select data-testid="crm-f-role" value={editForm.role || "user"} onChange={(e) => setEditForm({...editForm, role: e.target.value})}
                  className="w-full bg-transparent border border-white/20 px-3 py-3 text-white focus:outline-none focus:border-gold">
                  <option value="user" className="bg-noir">user</option>
                  <option value="vip" className="bg-noir">vip</option>
                  <option value="admin" className="bg-noir">admin</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-8">
              <button data-testid="crm-save-btn" onClick={saveEdit} className="bg-gold text-black font-accent uppercase tracking-[0.24em] text-xs px-7 py-3 hover:bg-ivory">Save</button>
              <button onClick={() => setEditing(null)} className="border border-white/30 text-white font-accent uppercase tracking-[0.24em] text-xs px-7 py-3 hover:border-white">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, v }) => (
  <div className="bg-onyx border border-white/10 p-6">
    <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-3">{label}</div>
    <div className="font-heading text-4xl text-ivory">{v}</div>
  </div>
);

const Select = ({ v, on, label, options, testid }) => (
  <select data-testid={testid} value={v} onChange={(e) => on(e.target.value)}
    className="bg-transparent border border-white/20 px-3 py-3 text-white focus:outline-none focus:border-gold text-sm">
    {options.map((o) => {
      const val = typeof o === "string" ? o : o.v;
      const lbl = typeof o === "string" ? o : o.l;
      return <option key={val} value={val} className="bg-noir">{label}: {lbl}</option>;
    })}
  </select>
);

export default CustomerCRM;
